import React from "react";
import { fetchInputsV3ToolbarSummary } from "../../services/inputsV3Service";

const POLL_INTERVAL_MS = 120_000;
const FOCUS_DEBOUNCE_MS = 15_000;

const listeners = new Set();

let state = {
  loading: false,
  pendingSummary: null,
  maintenance: null,
  claims: [],
  error: null,
};
let activeSubscribers = 0;
let pollTimer = null;
let focusTimer = null;
let inflight = null;
let lastFocusRefreshAt = 0;

function emit() {
  listeners.forEach((listener) => listener(state));
}

function setState(patch) {
  state = { ...state, ...patch };
  emit();
}

function normalizePayload(payload) {
  return {
    pendingSummary: payload?.pending_summary || null,
    maintenance: payload?.maintenance || null,
    claims: Array.isArray(payload?.claims) ? payload.claims : [],
    error: null,
  };
}

export function refreshInputsV3ToolbarSummary() {
  if (inflight) return inflight;
  setState({ loading: true });
  inflight = fetchInputsV3ToolbarSummary()
    .then((payload) => {
      setState({ loading: false, ...normalizePayload(payload) });
      return payload;
    })
    .catch((error) => {
      setState({
        loading: false,
        pendingSummary: null,
        maintenance: null,
        claims: [],
        error,
      });
      throw error;
    })
    .finally(() => {
      inflight = null;
    });
  return inflight;
}

function debouncedFocusRefresh() {
  if (focusTimer) clearTimeout(focusTimer);
  focusTimer = setTimeout(() => {
    const now = Date.now();
    if (now - lastFocusRefreshAt < FOCUS_DEBOUNCE_MS) return;
    lastFocusRefreshAt = now;
    refreshInputsV3ToolbarSummary().catch(() => {});
  }, 300);
}

function startSharedPolling() {
  refreshInputsV3ToolbarSummary().catch(() => {});
  pollTimer = setInterval(() => {
    refreshInputsV3ToolbarSummary().catch(() => {});
  }, POLL_INTERVAL_MS);
  window.addEventListener("focus", debouncedFocusRefresh);
  window.addEventListener("inputDecisionAnsweredV3", refreshFromEvent);
}

function stopSharedPolling() {
  if (activeSubscribers > 0) return;
  if (pollTimer) clearInterval(pollTimer);
  if (focusTimer) clearTimeout(focusTimer);
  pollTimer = null;
  focusTimer = null;
  window.removeEventListener("focus", debouncedFocusRefresh);
  window.removeEventListener("inputDecisionAnsweredV3", refreshFromEvent);
}

function refreshFromEvent() {
  refreshInputsV3ToolbarSummary().catch(() => {});
}

export default function useInputsV3ToolbarSummary() {
  const [snapshot, setSnapshot] = React.useState(state);

  React.useEffect(() => {
    const shouldStart = activeSubscribers === 0;
    activeSubscribers += 1;
    if (shouldStart) startSharedPolling();
    listeners.add(setSnapshot);
    setSnapshot(state);
    return () => {
      listeners.delete(setSnapshot);
      activeSubscribers -= 1;
      stopSharedPolling();
    };
  }, []);

  return {
    ...snapshot,
    refresh: refreshInputsV3ToolbarSummary,
  };
}
